// "Send newsletter" modal — opens from the editor's top bar / export
// affordance. Two paths: send a single test email, or fan out to all
// confirmed subscribers (optionally filtered by signup source slug).

import { useEffect, useMemo, useState } from 'react';
import { Send, X, AlertTriangle, Mail } from 'lucide-react';
import { ApiError, newsletterApi, signupPagesApi, subscribersApi } from '../lib/api';
import type { SignupPageListItem } from '@shared/types';

interface Props {
  draftId: string;
  defaultSubject: string;
  onClose: () => void;
}

export default function SendPanel({ draftId, defaultSubject, onClose }: Props) {
  const [subject, setSubject] = useState(defaultSubject);
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [pages, setPages] = useState<SignupPageListItem[]>([]);
  const [eligibleCount, setEligibleCount] = useState<number | null>(null);

  const [testEmail, setTestEmail] = useState('');
  const [testStatus, setTestStatus] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [testErr, setTestErr] = useState<string | null>(null);

  const [confirmFull, setConfirmFull] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [sentSummary, setSentSummary] = useState<{
    send_id: string;
    recipient_count: number;
    status: string;
  } | null>(null);

  // signup_pages now includes the 'homepage' row (migration 0006), so
  // we don't need to prepend it as a fixed option — just sort + dedupe.
  const sourceOptions = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const slug of pages.map((p) => p.slug).sort()) {
      if (seen.has(slug)) continue;
      seen.add(slug);
      out.push(slug);
    }
    return out;
  }, [pages]);

  useEffect(() => {
    void signupPagesApi
      .list()
      .then(({ pages }) => setPages(pages))
      .catch(() => setPages([]));
  }, []);

  // Eligible recipient count — re-query on source filter change.
  useEffect(() => {
    setEligibleCount(null);
    void subscribersApi
      .list({
        status: 'confirmed',
        source: sourceFilter || undefined,
        page: 1,
        page_size: 1,
      })
      .then((res) => setEligibleCount(res.total))
      .catch(() => setEligibleCount(null));
  }, [sourceFilter]);

  function makeIdempotencyKey(): string {
    return `${draftId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  async function sendTest() {
    if (!testEmail.trim()) {
      setTestErr('Enter an address to send the test to.');
      return;
    }
    if (!subject.trim()) {
      setTestErr('Subject is required.');
      return;
    }
    setTestStatus('sending');
    setTestErr(null);
    try {
      await newsletterApi.send(draftId, {
        idempotency_key: makeIdempotencyKey(),
        subject: subject.trim(),
        test_email: testEmail.trim(),
      });
      setTestStatus('sent');
    } catch (e) {
      setTestStatus('failed');
      setTestErr(e instanceof Error ? e.message : 'Test send failed');
    }
  }

  async function sendFull() {
    if (!subject.trim()) {
      setSendErr('Subject is required.');
      return;
    }
    setSending(true);
    setSendErr(null);
    try {
      const result = await newsletterApi.send(draftId, {
        idempotency_key: makeIdempotencyKey(),
        subject: subject.trim(),
        source_filter: sourceFilter || undefined,
      });
      if ('send_id' in result) {
        setSentSummary({
          send_id: result.send_id,
          recipient_count: result.recipient_count,
          status: result.status,
        });
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === 'conflict' && e.details) {
        setSendErr('A send with this idempotency key already exists.');
      } else {
        setSendErr(e instanceof Error ? e.message : 'Send failed');
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <header className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display text-lg text-slate-900 flex items-center gap-2">
            <Send size={16} /> Send newsletter
          </h2>
          <button onClick={onClose} className="btn-ghost p-1">
            <X size={16} />
          </button>
        </header>

        <div className="p-5 space-y-5">
          {sentSummary ? (
            <div className="text-sm">
              <p className="text-slate-900 font-medium mb-2">
                Sent! Status: <span className="text-blue-700">{sentSummary.status}</span>
              </p>
              <p className="text-slate-600">
                Sent to <strong>{sentSummary.recipient_count}</strong> subscriber
                {sentSummary.recipient_count === 1 ? '' : 's'}. Tracking events
                will populate as Resend delivers + recipients open/click.
              </p>
              <a
                href={`/sends/${sentSummary.send_id}`}
                className="text-blue-700 hover:underline block mt-3"
              >
                View send detail →
              </a>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Subject
                </label>
                <input
                  className="input w-full"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="What goes in the subject line"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Send to subscribers from
                </label>
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="input"
                >
                  <option value="">All confirmed subscribers</option>
                  {sourceOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  {eligibleCount === null
                    ? 'Counting…'
                    : `${eligibleCount} eligible subscriber${eligibleCount === 1 ? '' : 's'}.`}
                </p>
              </div>

              <details className="border border-slate-200 rounded-md p-3">
                <summary className="text-sm font-medium text-slate-900 cursor-pointer flex items-center gap-2">
                  <Mail size={14} /> Send a test first
                </summary>
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-slate-500">
                    Goes to a single address with a <code>[TEST]</code> subject prefix.
                    No <code>newsletter_sends</code> row, no tracking.
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="email"
                      className="input flex-1"
                      placeholder="me@example.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                    />
                    <button
                      onClick={sendTest}
                      disabled={testStatus === 'sending'}
                      className="btn-secondary text-sm"
                    >
                      {testStatus === 'sending' ? 'Sending…' : 'Send test'}
                    </button>
                  </div>
                  {testStatus === 'sent' && (
                    <p className="text-xs text-green-700">Test sent. Check your inbox.</p>
                  )}
                  {testErr && <p className="text-xs text-red-600">{testErr}</p>}
                </div>
              </details>

              {sendErr && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{sendErr}</span>
                </div>
              )}

              {!confirmFull ? (
                <button
                  onClick={() => setConfirmFull(true)}
                  disabled={!eligibleCount || !subject.trim()}
                  className="btn-primary w-full"
                >
                  Send to {eligibleCount ?? '…'} subscribers
                </button>
              ) : (
                <div className="border border-amber-200 bg-amber-50 rounded p-3 text-sm">
                  <p className="text-amber-900 mb-2">
                    Confirm: send <strong>"{subject}"</strong> to{' '}
                    <strong>{eligibleCount}</strong> subscribers? This is not
                    reversible.
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={sendFull}
                      disabled={sending}
                      className="btn-primary"
                    >
                      {sending ? 'Sending…' : 'Yes, send now'}
                    </button>
                    <button
                      onClick={() => setConfirmFull(false)}
                      disabled={sending}
                      className="btn-ghost text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
